import catalog from "./crestCatalog.json";
import { ACHIEVEMENTS_BY_ID } from "../stats/achievements";
import { levelProgress, MAX_LEVEL } from "../stats/levels";
import type { PlayerProfile } from "../stats/profile";
import { describeRule, evaluateUnlock, type UnlockRule, type UnlockState } from "./unlocks";

/**
 * The level crest: a player's level in an enamel medallion on their deck, a
 * frame round it, and three sockets for badges.
 *
 * Two kinds of thing are unlocked here and they unlock differently on purpose.
 *
 *   - FRAMES are ordinary cosmetics with an `UnlockRule`, exactly like a deck or
 *     a venue: six on the level ladder (each one keeps everything the last one
 *     drew and adds to it), and a few for a specific achievement or an event.
 *   - BADGES have a RIM - plastic, silver, gold, diamond (cut as an octagon) - read
 *     off how far up one achievement family a player has climbed. Diamond is
 *     that family's top tier, which is the one deliberate exception to the rule
 *     in unlocks.ts that no cosmetic waits on a top-tier achievement: a diamond
 *     rim is the show-off reward for the grind, and nothing about the game is
 *     behind it. Level badges sit on the levels that do not open a frame.
 *
 *     The icon inside a badge is made of its rim's material too, because at an
 *     8-seat phone deck the rim alone is a hairline nobody can read the color
 *     of. Level badges are the exception: each design owns its metal.
 *
 * The catalog itself is crestCatalog.json, because the game server reads the
 * same rules to check a signed-in player's crest against their cloud save
 * (scripts/gen-crest-catalog.mjs generates its copy). The art is
 * pixi/crestArt.ts.
 */

export type Rim = "plastic" | "silver" | "gold" | "diamond";
export const RIMS: readonly Rim[] = ["plastic", "silver", "gold", "diamond"];

/** How a rim is named on screen. */
export const RIM_LABEL: Record<Rim, string> = {
  plastic: "Plastic",
  silver: "Silver",
  gold: "Gold",
  diamond: "Diamond",
};

/**
 * Reads a rim off the wire. "bronze" is the old lowest rim, still sent by
 * clients from before the fourth tier; it is the same rung, so it is read as
 * plastic rather than dropped. Remove once no build sends it.
 */
function parseRim(raw: unknown): Rim | null {
  if (raw === "bronze") return "plastic";
  return typeof raw === "string" && (RIMS as readonly string[]).includes(raw) ? (raw as Rim) : null;
}

export interface CrestBadge {
  id: string;
  rim: Rim;
}

/** Everything needed to draw one player's crest. */
export interface Crest {
  level: number;
  frame: string;
  /** Left, right, bottom socket. */
  badges: [CrestBadge | null, CrestBadge | null, CrestBadge | null];
}

export interface FrameDef {
  id: string;
  name: string;
  rule: UnlockRule;
}

export interface BadgeDef {
  id: string;
  name: string;
  /** Achievement ids for the plastic, silver, gold and diamond rims; null where a family has no such tier. */
  tiers?: (string | null)[];
  /** A level badge: unlocked by reaching this level, rim by `levelBadgeRim`. */
  level?: number;
}

export const FRAMES: readonly FrameDef[] = catalog.frames as FrameDef[];
export const FRAME_BY_ID = new Map(FRAMES.map((f) => [f.id, f]));
export const STARTER_FRAME_ID = "pewter";

export const BADGES: readonly BadgeDef[] = catalog.badges as BadgeDef[];
export const BADGE_BY_ID = new Map(BADGES.map((b) => [b.id, b]));

/** The frames earned by levelling, lowest first. */
export const LADDER_FRAMES: readonly FrameDef[] = FRAMES.filter((f) => f.rule.kind === "starter" || f.rule.kind === "level");

export function frameUnlock(frame: FrameDef, profile: PlayerProfile): UnlockState {
  return evaluateUnlock(frame.rule, profile);
}

export function levelBadgeRim(level: number): Rim {
  if (level >= catalog.levelBadgeRims.diamondFrom) return "diamond";
  if (level >= catalog.levelBadgeRims.goldFrom) return "gold";
  if (level >= catalog.levelBadgeRims.silverFrom) return "silver";
  return "plastic";
}

/** The best rim this profile has earned on a badge, or null while it is locked. */
export function badgeRim(badge: BadgeDef, profile: PlayerProfile): Rim | null {
  if (badge.level !== undefined) return levelProgress(profile).level >= badge.level ? levelBadgeRim(badge.level) : null;
  let best: Rim | null = null;
  badge.tiers?.forEach((id, i) => {
    if (id && profile.achievements[id]) best = RIMS[i];
  });
  return best;
}

/** True when a badge is earned by a hidden achievement: a secret the shelves must not show before it is held. */
export function isSecretBadge(badge: BadgeDef): boolean {
  return (badge.tiers ?? []).some((id) => !!id && !!ACHIEVEMENTS_BY_ID.get(id)?.hidden);
}

/** The lowest rim a badge can be earned at — what a locked badge asks for first. */
export function firstRim(badge: BadgeDef): Rim {
  if (badge.level !== undefined) return levelBadgeRim(badge.level);
  return RIMS[badge.tiers?.findIndex((id) => !!id) ?? 0] ?? "plastic";
}

/** "Reach level 8", "Earn “Five Out!”" — the first step towards a badge. */
export function badgeRequirement(badge: BadgeDef): string {
  if (badge.level !== undefined) return describeRule({ kind: "level", level: badge.level });
  const first = badge.tiers?.find((id) => !!id);
  return first ? describeRule({ kind: "achievement", achievementId: first }) : "";
}

/** What earns each rim, for a badge's detail: [rim, achievement name] for every tier it has. */
export function badgeTierNames(badge: BadgeDef): [Rim, string][] {
  if (badge.level !== undefined) return [[levelBadgeRim(badge.level), `Level ${badge.level}`]];
  const out: [Rim, string][] = [];
  badge.tiers?.forEach((id, i) => {
    if (id) out.push([RIMS[i], ACHIEVEMENTS_BY_ID.get(id)?.name ?? id]);
  });
  return out;
}

/** The highest ladder frame this profile has reached — what a player wears until they choose. */
export function defaultFrameId(profile: PlayerProfile): string {
  let best = STARTER_FRAME_ID;
  for (const f of LADDER_FRAMES) if (frameUnlock(f, profile).unlocked) best = f.id;
  return best;
}

const RIM_RANK: Record<Rim, number> = { plastic: 0, silver: 1, gold: 2, diamond: 3 };

/**
 * The three best badges earned, for a player who has never chosen any:
 * highest rim first, catalog order breaking ties. A crest with three empty
 * sockets for somebody holding a gold badge would be the feature switched off.
 */
export function autoBadgeIds(profile: PlayerProfile): string[] {
  return BADGES.map((b, i) => ({ b, i, rim: badgeRim(b, profile) }))
    .filter((x): x is { b: BadgeDef; i: number; rim: Rim } => x.rim !== null)
    .sort((a, b) => RIM_RANK[b.rim] - RIM_RANK[a.rim] || a.i - b.i)
    .slice(0, 3)
    .map((x) => x.b.id);
}

// ---------------------------------------------------------------------------
// The player's choice. Kept in localStorage beside the deck and venue choice,
// and like them it is a preference, not a record: anything it names that the
// profile no longer holds is simply not worn (see `crestFor`).

export interface CrestSelection {
  /** Null until the player picks one: they wear `defaultFrameId`. */
  frame: string | null;
  /** Null until the player has touched a socket: they wear `autoBadgeIds`. */
  badges: (string | null)[] | null;
}

export const CREST_KEY = "fiveout:crest";
const listeners = new Set<() => void>();

export function getCrestSelection(): CrestSelection {
  try {
    const raw = JSON.parse(localStorage.getItem(CREST_KEY) ?? "null");
    if (raw && typeof raw === "object") {
      const frame = typeof raw.frame === "string" && FRAME_BY_ID.has(raw.frame) ? raw.frame : null;
      const badges = Array.isArray(raw.badges)
        ? [0, 1, 2].map((i) => (typeof raw.badges[i] === "string" && BADGE_BY_ID.has(raw.badges[i]) ? (raw.badges[i] as string) : null))
        : null;
      return { frame, badges };
    }
  } catch {
    // Unreadable storage is no choice made.
  }
  return { frame: null, badges: null };
}

export function setCrestSelection(selection: CrestSelection): void {
  try {
    localStorage.setItem(CREST_KEY, JSON.stringify(selection));
  } catch {
    // A private window that refuses storage keeps the choice for this session only.
  }
  for (const fn of listeners) fn();
}

/** Called whenever the selection changes. Returns the unsubscribe. */
export function subscribeCrest(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * The crest this profile wears: its level, the chosen frame if it is still
 * unlocked (else the best ladder frame), and the chosen badges at the rims
 * earned, with anything locked left as an empty socket.
 */
export function crestFor(profile: PlayerProfile, selection: CrestSelection = getCrestSelection()): Crest {
  const { level } = levelProgress(profile);
  const chosen = selection.frame ? FRAME_BY_ID.get(selection.frame) : undefined;
  const frame = chosen && frameUnlock(chosen, profile).unlocked ? chosen.id : defaultFrameId(profile);
  const ids = selection.badges ?? autoBadgeIds(profile);
  const seen = new Set<string>();
  const badges = [0, 1, 2].map((i) => {
    const id = ids[i];
    const def = id ? BADGE_BY_ID.get(id) : undefined;
    if (!def || seen.has(def.id)) return null;
    const rim = badgeRim(def, profile);
    if (!rim) return null;
    seen.add(def.id);
    return { id: def.id, rim };
  }) as Crest["badges"];
  return { level, frame, badges };
}

/**
 * A crest that arrived from somewhere else - another player, over the network -
 * cut down to what can be drawn: known frame and badge ids, a real rim, a level
 * on the ladder. Anything else makes the whole crest null rather than a guess.
 */
export function sanitizeCrest(raw: unknown): Crest | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const level = Number(r.level);
  if (!Number.isInteger(level) || level < 1 || level > MAX_LEVEL) return null;
  if (typeof r.frame !== "string" || !FRAME_BY_ID.has(r.frame)) return null;
  const list = Array.isArray(r.badges) ? r.badges : [];
  const badges = [0, 1, 2].map((i) => {
    const b = list[i] as Record<string, unknown> | null | undefined;
    if (!b || typeof b.id !== "string" || !BADGE_BY_ID.has(b.id)) return null;
    const rim = parseRim(b.rim);
    return rim ? { id: b.id, rim } : null;
  }) as Crest["badges"];
  return { level, frame: r.frame, badges };
}
